import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { createMeetingType, updateMeetingType } from "@/api/lookups";
import {
  LLM_MODEL_NAMES,
  type LLMModelName,
  type MeetingTypeResponse,
} from "@/api/types";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LlmModelsField } from "@/components/registries/LlmModelsField";
import {
  EMPTY_MEETING_TYPE_FORM,
  meetingTypeFormSchema,
  pickKnownModel,
  type MeetingTypeFormValues,
} from "@/components/registries/meetingTypeFormSchema";

export interface MeetingTypeFormDialogProps {
  open: boolean;
  existing: MeetingTypeResponse | null;
  onClose: () => void;
}

export function MeetingTypeFormDialog({
  open,
  existing,
  onClose,
}: MeetingTypeFormDialogProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const form = useForm<MeetingTypeFormValues>({
    resolver: zodResolver(meetingTypeFormSchema),
    defaultValues: EMPTY_MEETING_TYPE_FORM,
  });

  useEffect(() => {
    if (!open) return;
    if (existing === null) {
      form.reset(EMPTY_MEETING_TYPE_FORM);
      return;
    }
    form.reset({
      name: existing.name,
      description: existing.description ?? "",
      llm_models: existing.llm_models
        .map(pickKnownModel)
        .filter((m): m is LLMModelName => m !== undefined),
      consensus_model:
        existing.consensus_model !== null
          ? pickKnownModel(existing.consensus_model)
          : undefined,
      // TM-d6d6bf78 — pre-fill from the persisted system prompt
      // (or empty when the admin never set one).
      chat_system_prompt: existing.chat_system_prompt ?? "",
    });
  }, [open, existing, form]);

  const mutation = useMutation({
    mutationFn: async (values: MeetingTypeFormValues) => {
      const payload = {
        name: values.name,
        description:
          values.description === "" ? null : (values.description ?? null),
        llm_models: values.llm_models,
        consensus_model: values.consensus_model ?? null,
        // TM-d6d6bf78 — empty string ⇒ null so the backend clears
        // ``chat_system_prompt`` rather than persisting "".
        chat_system_prompt:
          values.chat_system_prompt === undefined ||
          values.chat_system_prompt === ""
            ? null
            : values.chat_system_prompt,
      };
      if (existing === null) return createMeetingType(payload);
      return updateMeetingType(existing.id, payload);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["meeting-types"] });
      onClose();
    },
  });

  const llmModels = form.watch("llm_models");
  const consensusValue = form.watch("consensus_model");

  const onToggleModel = (m: LLMModelName, checked: boolean) => {
    const current = form.getValues("llm_models");
    const next = checked
      ? Array.from(new Set([...current, m]))
      : current.filter((x) => x !== m);
    form.setValue("llm_models", next as LLMModelName[], {
      shouldValidate: true,
    });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? undefined : onClose())}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {existing === null
              ? t("adminMeetingTypes.createButton")
              : t("common.edit")}
          </DialogTitle>
        </DialogHeader>
        <form
          className="space-y-3"
          onSubmit={form.handleSubmit((v) => mutation.mutate(v))}
          data-testid="meeting-type-form"
        >
          <div>
            <Label htmlFor="mt-name">
              {t("adminMeetingTypes.fields.name")}
            </Label>
            <Input id="mt-name" {...form.register("name")} />
            {form.formState.errors.name !== undefined ? (
              <p className="text-xs text-destructive">
                {t("adminMeetingTypes.errors.nameRequired")}
              </p>
            ) : null}
          </div>
          <div>
            <Label htmlFor="mt-description">
              {t("adminMeetingTypes.fields.description")}
            </Label>
            <Input id="mt-description" {...form.register("description")} />
          </div>
          <div>
            <Label htmlFor="mt-chat-system-prompt">
              {t("meetingTypes.fields.chatSystemPrompt")}
            </Label>
            <textarea
              id="mt-chat-system-prompt"
              className="min-h-24 w-full rounded-md border bg-transparent px-3 py-2 text-sm"
              maxLength={10000}
              placeholder={t("meetingTypes.fields.chatSystemPromptPlaceholder")}
              {...form.register("chat_system_prompt")}
              data-testid="meeting-type-chat-system-prompt"
            />
          </div>
          <LlmModelsField
            selected={llmModels}
            hasError={form.formState.errors.llm_models !== undefined}
            onToggle={onToggleModel}
          />
          <div>
            <Label htmlFor="mt-consensus">
              {t("adminMeetingTypes.fields.consensusModel")}
            </Label>
            <select
              id="mt-consensus"
              className="h-9 w-full rounded-md border bg-background px-2 text-sm"
              value={consensusValue ?? ""}
              onChange={(e) => {
                const v = e.target.value;
                form.setValue(
                  "consensus_model",
                  v === "" ? undefined : (v as LLMModelName),
                  { shouldValidate: true },
                );
              }}
            >
              <option value="">{t("common.dash")}</option>
              {LLM_MODEL_NAMES.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
            {form.formState.errors.consensus_model !== undefined ? (
              <p className="text-xs text-destructive">
                {t("adminMeetingTypes.errors.consensusRequired")}
              </p>
            ) : null}
          </div>
          {mutation.isError ? (
            <p className="text-xs text-destructive" role="alert">
              {t("adminMeetingTypes.errors.saveFailed")}
            </p>
          ) : null}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? t("common.saving") : t("common.save")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
